import React, { useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import clsx from 'clsx';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import grey from '@material-ui/core/colors/grey';
import TableRow from '@material-ui/core/TableRow';
import Checkbox from '@material-ui/core/Checkbox';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import IconButton from '@material-ui/core/IconButton';
import MailIcon from '../../../../../icons/MailIcon';
import ShareReportTableCell from './ShareReportTableCell';
import Tooltip from '../../../../../Tooltip';
import TypographyParagraph from '../../../../../TypographyParagraph';

const useStyles = makeStyles((theme) => ({
  standardRow: {
    height: 40,
  },
  oddRowBackground: {
    backgroundColor: '#fafafa',
  },
  rowBorderRadius: {
    '& > :first-child': {
      borderTopLeftRadius: 3,
      borderBottomLeftRadius: 3,
    },
    '& > :last-child': {
      borderTopRightRadius: 3,
      borderBottomRightRadius: 3,
    },
  },
  checkbox: {
    padding: 5,
  },
  checkboxIcon: {
    width: 17,
    height: 17,
    borderRadius: 3,
    border: `2px solid ${grey[300]}`,
    backgroundColor: '#ffffff',
    margin: 2,
  },
  iconButton: {
    marginRight: 1,
    marginLeft: theme.spacing(2),
  },
  icon: {
    color: grey[500],
    display: 'flex',
  },
  shareHistory: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      color: grey[300],
      margin: '-1px 6px 0px 0px',
      fontSize: 20,
    }
  },
  tooltipLine: {
    display: 'flex',
  },
  tooltipText: {
    fontSize: 13,
    color: '#ffffff',
    marginBottom: 0,
    marginRight: 3,
    fontWeight: 300,
    whiteSpace: 'nowrap',
  },
  tooltipTextItalicized: {
    fontStyle: 'italic',
    opacity: 0.6,
    marginRight: 6,
  }
}));

function ShareReportTableRow({ row = {}, index, setSelected, isItemSelected, setRowMenu }) {
  const classes = useStyles();
  const timesShared = (row.sharehistory && row.sharehistory.length) || 0;
  const lastShared = timesShared ? row.sharehistory[row.sharehistory.length - 1] : null;
  const lastSharedMoment = lastShared && moment(lastShared.sentOn);
  const tooltipTitle = (
    <React.Fragment>
      {timesShared && row.sharehistory.map(obj => (
        <div key={obj.sentOn} className={classes.tooltipLine}>
          <TypographyParagraph className={clsx(classes.tooltipText, classes.tooltipTextItalicized)}>Sent</TypographyParagraph>
          <TypographyParagraph className={classes.tooltipText}>{moment(obj.sentOn).format('MMM D, YYYY')}</TypographyParagraph>
          <TypographyParagraph className={clsx(classes.tooltipText, classes.tooltipTextItalicized)}>at</TypographyParagraph>
          <TypographyParagraph className={classes.tooltipText}>{moment(obj.sentOn).format('h:m a')}</TypographyParagraph>
        </div>
      ))}
    </React.Fragment>
  )

  const handleOpenMenu = useCallback(event => {
    event.stopPropagation();
    setRowMenu({ anchorEl: event.currentTarget, email: row.email });
  }, []);

  const handleCheckboxClick = useCallback((event, email) => {
    setSelected(prevState => {
      const selectedIndex = prevState.indexOf(email);
      let newSelected = [];
  
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(prevState, email);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(prevState.slice(1));
      } else if (selectedIndex === prevState.length - 1) {
        newSelected = newSelected.concat(prevState.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          prevState.slice(0, selectedIndex),
          prevState.slice(selectedIndex + 1),
        );
      }
      return newSelected;
    });
  }, []);

  return (
    <TableRow
      hover
      className={classnames(classes.standardRow, classes.rowBorderRadius, { [classes.oddRowBackground]: index%2 !== 0 })}
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      selected={isItemSelected}
    >
      {!row.inherited && useMemo(() => (
        <ShareReportTableCell variant="body" padding="checkbox" data-checkbox>
          <Checkbox
            onChange={event => handleCheckboxClick(event, row.email)}
            color="primary"
            icon={<span className={classes.checkboxIcon} />}
            classes={{ root: classes.checkbox }}
            checked={isItemSelected}
          />
        </ShareReportTableCell>
      ), [isItemSelected])}
      {useMemo(() => (
        <React.Fragment>
          <ShareReportTableCell variant="body" align="left" data-email inherited={!!row.inherited}>
            {row.email}
          </ShareReportTableCell>
          <ShareReportTableCell variant="body" align="center" data-times-shared inherited={!!row.inherited}>
            <Tooltip title={timesShared ? tooltipTitle : ''}>
              <div className={classes.shareHistory}>
                <MailIcon />
                {timesShared}
              </div>
            </Tooltip>
          </ShareReportTableCell>
          <ShareReportTableCell variant="body" align="center" data-last-sent inherited={!!row.inherited}>
            {lastSharedMoment ? lastSharedMoment.format('MMM D, YYYY') : '--'}
          </ShareReportTableCell>
          <ShareReportTableCell variant="body" padding="checkbox" data-menu inherited={!!row.inherited}>
            <IconButton className={classes.iconButton} size="small" onClick={handleOpenMenu}>
              <MoreVertIcon className={classes.icon} />
            </IconButton>
          </ShareReportTableCell>
        </React.Fragment>
      ), [])}
    </TableRow>
  );
}

ShareReportTableRow.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  setSelected: PropTypes.func,
  isItemSelected: PropTypes.bool.isRequired,
  setRowMenu: PropTypes.func.isRequired,
}

export default memo(ShareReportTableRow);


// WEBPACK FOOTER //
// ./src/components/SRHHeaders/SummaryReportHeader/components/ShareReport/components/ShareReportTableRow.jsx